import { getCanvasViewportDimensions } from "@/lib/viewport-dimensions";
import { getWindowOrigin } from "@/desktop/use-window-origin";
import { useCanvasStore } from "@/stores/canvas-store";
import { getCurrentDenkFocusBox } from "@/lib/presentation-mode";
import type {
  PresentPlacement,
  PresentPoint,
  ScreenRect,
} from "@/lib/canvas-presentation-types";

export const PRESENT_GAP = 32;
const CONTENT_FIT_INSET = 24;
const CONTENT_MIN_W = 320;
const CONTENT_MIN_H = 240;

const PRESENT_SIDE_MIN_W = 380;
const PRESENT_SIDE_MIN_H = 560;
const PRESENT_BAR_MIN_W = 760;
const PRESENT_BAR_MIN_H = 320;
const PRESENT_CENTER_MIN_W = 520;
const PRESENT_CENTER_MIN_H = 420;

export function currentFlowCenter() {
  const { viewport } = useCanvasStore.getState();
  const zoom = viewport.zoom || 1;
  const { viewportWidth, viewportHeight } = getCanvasViewportDimensions();
  return {
    x: (-viewport.x + viewportWidth / 2) / zoom,
    y: (-viewport.y + viewportHeight / 2) / zoom,
  };
}

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function flowPointForScreenPoint(point: PresentPoint) {
  const state = useCanvasStore.getState();
  const origin = getWindowOrigin();
  const clientPoint = { x: point.x - origin.x, y: point.y - origin.y };
  if (state.screenToFlowPosition) {
    return state.screenToFlowPosition(clientPoint);
  }
  const zoom = state.viewport.zoom || 1;
  return {
    x: (clientPoint.x - state.viewport.x) / zoom,
    y: (clientPoint.y - state.viewport.y) / zoom,
  };
}

export function rectForVisibleFlowViewport(): ScreenRect {
  const { viewport } = useCanvasStore.getState();
  const center = currentFlowCenter();
  const zoom = viewport.zoom || 1;
  const { viewportWidth, viewportHeight } = getCanvasViewportDimensions();
  const width = viewportWidth / zoom;
  const height = viewportHeight / zoom;
  return {
    left: center.x - width / 2,
    top: center.y - height / 2,
    width,
    height,
  };
}

export function resolveAutoSide(
  viewport: ScreenRect,
  anchor: { x: number; y: number },
  preferred?: PresentPlacement,
): Exclude<PresentPlacement, "auto" | "center" | "cursor"> {
  const candidates: Array<
    Exclude<PresentPlacement, "auto" | "center" | "cursor">
  > =
    preferred &&
    preferred !== "auto" &&
    preferred !== "center" &&
    preferred !== "cursor"
      ? [preferred]
      : ["right", "left", "bottom", "top"];
  const spaces = {
    right: viewport.left + viewport.width - anchor.x,
    left: anchor.x - viewport.left,
    bottom: viewport.top + viewport.height - anchor.y,
    top: anchor.y - viewport.top,
  };
  for (const side of candidates) {
    const size = presentSizeForSide(side);
    const needed =
      side === "left" || side === "right" ? size.width : size.height;
    if (spaces[side] >= needed + PRESENT_GAP * 2) return side;
  }
  return (Object.entries(spaces).sort((a, b) => b[1] - a[1])[0]?.[0] ??
    "right") as Exclude<PresentPlacement, "auto" | "center" | "cursor">;
}

function cappedDimension(
  preferred: number,
  minimum: number,
  available?: number,
) {
  const desired = Math.max(minimum, preferred);
  if (available == null) return desired;
  if (available <= 0) return desired;
  return Math.min(desired, available);
}

export function presentSizeForSide(
  side: PresentPlacement,
  viewport?: ScreenRect,
  anchor?: { x: number; y: number },
) {
  const focusBox = getCurrentDenkFocusBox();
  const horizontalSpace =
    viewport && anchor
      ? side === "left"
        ? anchor.x - viewport.left - PRESENT_GAP * 2
        : viewport.left + viewport.width - anchor.x - PRESENT_GAP * 2
      : undefined;
  const verticalSpace =
    viewport && anchor
      ? side === "top"
        ? anchor.y - viewport.top - PRESENT_GAP * 2
        : viewport.top + viewport.height - anchor.y - PRESENT_GAP * 2
      : undefined;
  const viewportHeight =
    viewport != null ? viewport.height - PRESENT_GAP * 2 : undefined;
  const viewportWidth =
    viewport != null ? viewport.width - PRESENT_GAP * 2 : undefined;

  if (side === "left" || side === "right" || side === "cursor") {
    return {
      width: cappedDimension(
        focusBox.width,
        PRESENT_SIDE_MIN_W,
        horizontalSpace,
      ),
      height: cappedDimension(
        focusBox.height,
        PRESENT_SIDE_MIN_H,
        viewportHeight,
      ),
    };
  }
  if (side === "top" || side === "bottom") {
    return {
      width: cappedDimension(focusBox.width, PRESENT_BAR_MIN_W, viewportWidth),
      height: cappedDimension(
        focusBox.height,
        PRESENT_BAR_MIN_H,
        verticalSpace,
      ),
    };
  }
  return {
    width: cappedDimension(focusBox.width, PRESENT_CENTER_MIN_W, viewportWidth),
    height: cappedDimension(
      focusBox.height,
      PRESENT_CENTER_MIN_H,
      viewportHeight,
    ),
  };
}

/**
 * Where a content fit goes: an agent's placement, or `dock` for a small
 * prompt (a Connect card) that sits at the right edge, out of the way.
 */
export type ContentFitPlacement = PresentPlacement | "dock";

export interface ContentAwareFrameFitInput {
  surface: ScreenRect;
  desired: { width: number; height: number; aspectRatio?: number };
  exclusions?: ScreenRect[];
  placement?: ContentFitPlacement;
  inset?: number;
  gap?: number;
}

interface PlacementCandidate extends ScreenRect {
  side: "center" | "left" | "right" | "top" | "bottom";
}

function rectRight(rect: ScreenRect): number {
  return rect.left + rect.width;
}

function rectBottom(rect: ScreenRect): number {
  return rect.top + rect.height;
}

function collisionArea(a: ScreenRect, b: ScreenRect): number {
  const width = Math.max(
    0,
    Math.min(rectRight(a), rectRight(b)) - Math.max(a.left, b.left),
  );
  const height = Math.max(
    0,
    Math.min(rectBottom(a), rectBottom(b)) - Math.max(a.top, b.top),
  );
  return width * height;
}

function fitContentSize(
  desired: ContentAwareFrameFitInput["desired"],
  availableWidth: number,
  availableHeight: number,
) {
  const maxWidth = Math.max(1, availableWidth);
  const maxHeight = Math.max(1, availableHeight);
  const minWidth = Math.min(CONTENT_MIN_W, maxWidth);
  const minHeight = Math.min(CONTENT_MIN_H, maxHeight);
  const ratio =
    desired.aspectRatio && desired.aspectRatio > 0
      ? desired.aspectRatio
      : undefined;
  if (ratio) {
    let width = clamp(desired.width, minWidth, maxWidth);
    let height = width / ratio;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * ratio;
    }
    if (width < minWidth && minWidth / ratio <= maxHeight) {
      width = minWidth;
      height = width / ratio;
    }
    return { width: Math.round(width), height: Math.round(height) };
  }
  return {
    width: Math.round(clamp(desired.width, minWidth, maxWidth)),
    height: Math.round(clamp(desired.height, minHeight, maxHeight)),
  };
}

function candidateForSide(
  side: PlacementCandidate["side"],
  surface: ScreenRect,
  subject: ScreenRect | undefined,
  desired: ContentAwareFrameFitInput["desired"],
  inset: number,
  gap: number,
): PlacementCandidate {
  const inner = {
    left: surface.left + inset,
    top: surface.top + inset,
    width: Math.max(1, surface.width - inset * 2),
    height: Math.max(1, surface.height - inset * 2),
  };
  const innerRight = rectRight(inner);
  const innerBottom = rectBottom(inner);
  let availableWidth = inner.width;
  let availableHeight = inner.height;
  if (subject && side === "right") {
    availableWidth = innerRight - rectRight(subject) - gap;
  } else if (subject && side === "left") {
    availableWidth = subject.left - inner.left - gap;
  } else if (subject && side === "bottom") {
    availableHeight = innerBottom - rectBottom(subject) - gap;
  } else if (subject && side === "top") {
    availableHeight = subject.top - inner.top - gap;
  }
  const size = fitContentSize(
    desired,
    Math.max(1, availableWidth),
    Math.max(1, availableHeight),
  );
  let left = inner.left + (inner.width - size.width) / 2;
  let top = inner.top + (inner.height - size.height) / 2;
  if (subject) {
    if (side === "right") left = rectRight(subject) + gap;
    if (side === "left") left = subject.left - gap - size.width;
    if (side === "bottom") top = rectBottom(subject) + gap;
    if (side === "top") top = subject.top - gap - size.height;
    if (side === "left" || side === "right") {
      top = subject.top + (subject.height - size.height) / 2;
    } else if (side === "top" || side === "bottom") {
      left = subject.left + (subject.width - size.width) / 2;
    }
  }
  return {
    side,
    left: clamp(left, inner.left, innerRight - size.width),
    top: clamp(top, inner.top, innerBottom - size.height),
    ...size,
  };
}

/** A docked prompt: its fitted size at the right edge, vertically centred. */
function dockedFrameBox(
  surface: ScreenRect,
  desired: ContentAwareFrameFitInput["desired"],
  inset: number,
) {
  const inner = {
    width: Math.max(1, surface.width - inset * 2),
    height: Math.max(1, surface.height - inset * 2),
  };
  const size = fitContentSize(desired, inner.width, inner.height);
  return {
    x: Math.round(surface.left + inset + inner.width - size.width),
    y: Math.round(surface.top + inset + (inner.height - size.height) / 2),
    ...size,
  };
}

/**
 * Fits one rich frame into the visible surface while avoiding its subject.
 * A docked prompt keeps the right edge whatever else is on screen.
 */
export function calculateContentAwareFrameBox({
  surface,
  desired,
  exclusions = [],
  placement = "auto",
  inset = CONTENT_FIT_INSET,
  gap = PRESENT_GAP,
}: ContentAwareFrameFitInput) {
  if (placement === "dock") return dockedFrameBox(surface, desired, inset);
  const subject = exclusions[0];
  const sides: PlacementCandidate["side"][] = [
    "right",
    "left",
    "bottom",
    "top",
    "center",
  ];
  const requested =
    placement === "cursor" || placement === "auto" ? undefined : placement;
  const order = requested
    ? [requested, ...sides.filter((side) => side !== requested)]
    : sides;
  const candidates = order.map((side) =>
    candidateForSide(side, surface, subject, desired, inset, gap),
  );
  // A narration bubble must not turn a reading surface into a sliver. Permit
  // modest fitting around annotations, but preserve readable dimensions even
  // when avoiding an exclusion entirely is impossible on this display.
  const readableSize = fitContentSize(
    desired,
    surface.width - inset * 2,
    surface.height - inset * 2,
  );
  const readableCandidates = candidates.filter(
    (candidate) =>
      candidate.width >= readableSize.width * 0.85 &&
      candidate.height >= readableSize.height * 0.85,
  );
  const desiredArea = Math.max(1, desired.width * desired.height);
  const best = readableCandidates.reduce(
    (current, candidate, index) => {
      const collisions = exclusions.reduce(
        (total, exclusion) => total + collisionArea(candidate, exclusion),
        0,
      );
      const areaLoss =
        1 - Math.min(1, (candidate.width * candidate.height) / desiredArea);
      const score = collisions * 1_000_000 + areaLoss * 10_000 + index * 100;
      return !current || score < current.score ? { candidate, score } : current;
    },
    null as { candidate: PlacementCandidate; score: number } | null,
  )!;
  return {
    x: Math.round(best.candidate.left),
    y: Math.round(best.candidate.top),
    width: best.candidate.width,
    height: best.candidate.height,
  };
}
